import { useState } from 'react';
import toast from 'react-hot-toast';
import { RIDialog } from '../../../_core/dialog/dialog.component';
import { MultiSelectInput, IMultiSelectOption } from '../../../_core/input/multiselect-input.component';
import { Button } from '../../../_core/button/button.component';
import { IRequest, IClientOverview } from '../../../../lib/types';
import { useGetClientLinkToken } from '../../access-token/access-token.service';
import { ROUTE_PATHS } from '../../../../_routes';
import { getName } from '../../../../utils/name.utils';

interface ICopyClientLinkDialogProps {
  onClose: () => void;
  request: IRequest;
}

const getUniqueClients = (request: IRequest): IClientOverview[] => {
  const seen = new Set<string>();
  const clients: IClientOverview[] = [];

  const candidates: (IClientOverview | undefined)[] = [
    request.assignedTo,
    ...(request.clientEditorIds ?? []),
    ...(request.clientViewerIds ?? [])
  ];

  for (const client of candidates) {
    if (client && !seen.has(client._id)) {
      seen.add(client._id);
      clients.push(client);
    }
  }

  return clients;
};

export const CopyClientLinkDialog: React.FC<ICopyClientLinkDialogProps> = ({ onClose, request }) => {
  const { getClientLinkToken, loading } = useGetClientLinkToken();
  const [selected, setSelected] = useState<IMultiSelectOption[]>([]);

  const clients = getUniqueClients(request);

  const clientOptions: IMultiSelectOption[] = clients.map((c) => ({
    value: c.user._id,
    label: getName(c),
    secondaryLabel: c.user.email
  }));

  const handleCopy = async () => {
    const userId = selected[0]?.value;
    if (!userId) return;

    try {
      const result = await getClientLinkToken(request._id, userId);
      const token = result.data?.getMagicLinkTokenForRequest;

      if (token) {
        const url = `${window.location.origin}${ROUTE_PATHS.ASSIGNED_REQUEST}/${request._id}?token=${token}`;
        await navigator.clipboard.writeText(url);
        toast.success('Client link copied');
      } else {
        toast.error('Failed to generate client link');
      }
    } catch {
      toast.error('Failed to copy client link');
    } finally {
      onClose();
    }
  };

  return (
    <RIDialog setOpen={(o) => { if (!o) onClose(); }}>
      <div className="p-6 space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Copy Client Link</h3>
        <p className="text-sm text-gray-500">Select a client to generate their client link.</p>
        <MultiSelectInput
          options={clientOptions}
          value={selected}
          setValue={setSelected}
          singleSelect
          placeholder="Search clients..."
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" text="Cancel" size="large" onClick={onClose} />
          <Button
            text="Copy Link"
            size="large"
            disabled={!selected.length}
            loading={loading}
            onClick={handleCopy}
          />
        </div>
      </div>
    </RIDialog>
  );
};
